/* eslint-disable max-statements */
import { useEffect } from 'react';
import { CategoryDetailsResponseDto } from '@tiendanube/common';
import useScrollToError from 'App/components/AppLayout/useScrollToError';
import { FEATURE_HIDE_CATEGORIES } from 'App/features';
import { useNavegate } from 'App/hooks';
import { CancelAndConfirmButtons, Stack } from 'commons/components';
import { CATALOG_EDIT_CATEGORIES } from 'config/upsellFlowSources';
import { useUpsellFlow } from 'domains/Billing/UpsellFlow/hooks';
import { Visibility } from 'domains/Catalog/Categories/components';
import { useCategories } from 'domains/Catalog/Categories/hooks';
import { useTranslationCatalog } from 'domains/Catalog/hooks';
import {
  GoogleShoppingCategories,
  SeoCategories,
} from 'domains/Catalog/Products/pages/components/Categories/components';
import { isCategoryVisible } from 'domains/Catalog/Products/utils';
import useCategory, {
  ImageInterface,
} from '../../hooks/useCategory/useCategory';
import FeaturedImageCard from '../FeaturedImageCard';
import InformationEditCategoryCard from '../InformationEditCategoryCard';
import SectionCategoriesMetafieldsExisting from '../SectionCategoriesMetafieldsExisting';

interface FormEditCategoryProps {
  values: CategoryDetailsResponseDto;
  errors: Partial<Record<string, string>>;
  isDirty: boolean;
  isSaving: boolean;
  setFieldValue: (name: string, value: any) => void;
  categoryOrSubcategory: 'category' | 'subcategory';
  handleOnSubmit: (e: React.SyntheticEvent<HTMLFormElement>) => void;
  id: string;
  autoScrollToSeo?: boolean;
}
function FormEditCategory({
  values,
  errors,
  isDirty,
  isSaving,
  setFieldValue,
  categoryOrSubcategory,
  handleOnSubmit,
  id,
  autoScrollToSeo = false,
}: FormEditCategoryProps) {
  const t = useTranslationCatalog();
  const { goBack } = useNavegate();

  const { uploadImage } = useCategory(id);

  const { categories, isSuccess, fetchCategoryOptions } = useCategories();

  useScrollToError(errors);

  const titleInformationEdit = t(
    `categories.detail.categoryInformation.${categoryOrSubcategory}`,
  );

  const handleOnHideVisibility = useUpsellFlow({
    title: t('categories.detail.visibility.upsell'),
    featureKey: FEATURE_HIDE_CATEGORIES,
    trackingSource: CATALOG_EDIT_CATEGORIES,
    callback: ({ name, value }) => setFieldValue(name, value),
  });

  const handleChangeVisibility = ({ name, value }) =>
    value === 'hidden'
      ? handleOnHideVisibility({ name, value })
      : setFieldValue(name, value);

  const handleOnChange = ({ name, value }) => {
    setFieldValue(name, value);
  };

  const handleChangeImage = (image: ImageInterface | null) => {
    setFieldValue('image', image);
  };

  const handleChangeMetafields = (metafields) => {
    setFieldValue('metafields', metafields);
  };

  const parent = categories.find((current) => current.id === values.parent);
  const hasParentHidden = parent ? !isCategoryVisible(parent) : false;
  const hasChildren = categories.some((current) => current.parent === id);

  useEffect(() => {
    fetchCategoryOptions();
  }, [fetchCategoryOptions]);

  return (
    <form onSubmit={handleOnSubmit}>
      <Stack column spacing="loose" align="stretch">
        <InformationEditCategoryCard
          id={id}
          title={titleInformationEdit}
          values={values}
          errors={errors}
          handleChange={handleOnChange}
        />
        <FeaturedImageCard
          image={values.image}
          uploadImage={uploadImage}
          onChangeImage={handleChangeImage}
        />
        <GoogleShoppingCategories
          categoryId={id}
          name={values.name}
          description={values.description}
          googleShoppingCategory={values.googleShoppingCategory}
          handleChange={handleOnChange}
          errors={errors}
        />
        <SeoCategories
          seoTitle={values.seoTitle}
          seoDescription={values.seoDescription}
          categoryId={id}
          name={values.name}
          description={values.description}
          handle={values.handle}
          handleChange={handleOnChange}
          errors={errors}
          autoScrollToSeo={autoScrollToSeo}
        />
        <SectionCategoriesMetafieldsExisting
          selecteds={values.metafields?.internals || []}
          apiSelecteds={values.metafields?.fromApi || []}
          onChange={handleChangeMetafields}
          id={id}
        />
        {isSuccess && (
          <Visibility
            hasChildren={hasChildren}
            visibility={values.visibility}
            onChange={handleChangeVisibility}
            disabled={hasParentHidden}
          />
        )}
        <CancelAndConfirmButtons
          confirmText={t('categories.detail.header.save')}
          cancelText={t('categories.detail.header.cancel')}
          onCancel={goBack}
          isConfirmDisabled={isSaving || !isDirty}
          isLoading={isSaving}
          type="submit"
        />
      </Stack>
    </form>
  );
}

export default FormEditCategory;
